import axios, { AxiosResponse } from "axios";
import {
  Buyer,
  CheckoutSessionResponse,
  EmbedTokenRequest,
  EmbedTokenResponse,
  GetTransactionsResponse,
  GoogleSessionResponse,
  JwtCheckoutData,
  LineItem,
  ListAllGr4vyPaymentMethodsResponse,
  PaymentMethod,
  ProductData,
  ProductDataResponse,
  SecureCheckoutResponse,
  SecureCheckoutResponseWithMedicationName,
  ShippingDetails,
  TransactionData,
  TransactionRequest,
  TransactionResponse,
  UserDataResponse,
} from "../typings/paymentsServiceTypes";
import { User } from "firebase/auth";

// Axios Setup

const getPaymentsBaseUrl = (): string => {
  return import.meta.env.VITE_PAYMENTS_API_URL;
};

const axiosInstance = axios.create({
  baseURL: getPaymentsBaseUrl(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

/**
 * Gets the embedded token for Gr4vy form.
 * @param productName - The name of the procut.
 * @param futurhealthPlusId - The ID of the FH user.
 * @param buyer - The buyer information.
 * @param currentUser - The current authenticated user.
 * @param shippingDetails - The shipping details.
 * @returns The embedded token response.
 */
async function getGr4vyEmbedToken(
  productName: string,
  futurhealthPlusId: string,
  buyer: Buyer,
  user: User | null,
  shippingDetails: ShippingDetails
): Promise<{ res: EmbedTokenResponse; medicationName: string }> {
  try {
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const ret = await getProductData({ productName, user });

    const [
      {
        id,
        edges: { line_items: lineItemList },
        medication_name: medicationName,
      },
    ] = ret.data;

    // Filter and sort line item IDs
    const lineItemIDs = lineItemList
      .filter((item: LineItem) => item.billing_cycle === "initial")
      .sort((item1, item2) => item1.display_order - item2.display_order)
      .map((item: LineItem) => item.id as string);

    const requestData: EmbedTokenRequest = {
      buyer,
      shipping_details: shippingDetails,
      line_item_ids: lineItemIDs,
      futurhealth_plus_id: futurhealthPlusId,
      product_id: id,
    };

    const response: AxiosResponse<EmbedTokenResponse> =
      await axiosInstance.post("/v1/gr4vy/embed", requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

    return { res: response.data, medicationName };
  } catch (error) {
    console.error("Error getting embed token:", error);
    throw error;
  }
}

export const getGr4vyEmbedTokenForZeroAuth = async (
  buyer_id: string,
  user: User | null,
  checkout_session_id: string
) => {
  if (!buyer_id || !checkout_session_id) {
    throw new Error("buyer_id and checkout_session_id are required");
  }

  const token = await user?.getIdToken();

  if (!token) {
    throw new Error("User is not authenticated");
  }

  const requestData = {
    buyer_id,
    currency: "USD",
    amount: 100,
    checkout_session_id,
    intent: "authorize",
  };

  const response: AxiosResponse<EmbedTokenResponse> = await axiosInstance.post(
    "/v1/gr4vy/embed-token",
    requestData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return { res: response.data };
};

interface GetUserDataParams {
  futurhealthPlusId?: string;
  user: User | null;
}

/**
 * Gets user data from the payments service.
 * @param futurhealthPlusId - The ID of the FH user.
 * @param user - The current authenticated user.
 * @returns The user data response.
 */
const getUserData = async ({
  futurhealthPlusId,
  user,
}: GetUserDataParams): Promise<UserDataResponse> => {
  try {
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const response: AxiosResponse<UserDataResponse> = await axiosInstance.get(
      "/v1/users",
      {
        params: {
          futurhealth_plus_id: futurhealthPlusId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

interface GetProductDataParams {
  productName?: string;
  productId?: string;
  user: User | null;
}

/**
 * Gets all product data and filters by product name if passed as a parameter.
 * @param productName - The name of the product to filter by (optional).
 * @param productId - The id of the product to filter by (optional).
 * @returns All data or filtered data are first in the returned array.
 */
const getProductData = async ({
  productName,
  productId,
  user,
}: GetProductDataParams): Promise<ProductDataResponse> => {
  try {
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    // Determine query parameter based on availability
    const queryParam = productId
      ? { id: productId }
      : productName
        ? { name: productName }
        : null;

    const response: AxiosResponse<ProductDataResponse> =
      await axiosInstance.get("/v1/products", {
        params: queryParam,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

    return response.data;
  } catch (error) {
    console.error("Error getting product data:", error);
    throw error;
  }
};

interface GetProductByIdParams {
  productId?: string;
  user: User | null;
}

/**
 * Gets information about a single product by ID.
 * @param productId - The ID of the product.
 * @returns The product information.
 */
const getProductById = async ({
  productId,
  user,
}: GetProductByIdParams): Promise<ProductData> => {
  try {
    if (!user) throw new Error("User is not authenticated");

    if (!productId) throw new Error("productId is required");

    const token = await user.getIdToken();

    const response: AxiosResponse<ProductData> = await axiosInstance.get(
      `/v1/products/${productId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting product data:", error);
    throw error;
  }
};

/**
 * Gets the embedded token for Gr4vy form.
 * @param productName - The name of the procut.
 * @param futurhealthPlusId - The ID of the FH user.
 * @param buyer - The buyer information.
 * @param currentUser - The current authenticated user.
 * @param shippingDetails - The shipping details.
 * @param hasDiscount - if true then discount is applied for the selected product.
 * @returns The embedded token response.
 */
async function getSecureFieldCheckout(
  productName: string,
  futurhealthPlusId: string,
  buyer: Buyer,
  user: User | null,
  shippingDetails: ShippingDetails,
  hasDiscount: boolean = false
): Promise<SecureCheckoutResponseWithMedicationName> {
  try {
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const ret = await getProductData({ productName, user });

    const [
      {
        id,
        edges: { line_items: lineItemList, discounts },
        medication_name: medicationName,
      },
    ] = ret.data;

    // Filter and sort line item IDs
    const lineItemIDs = lineItemList
      .filter((item: LineItem) => item.billing_cycle === "initial")
      .sort((item1, item2) => item1.display_order - item2.display_order)
      .map((item: LineItem) => item.id as string);

    const discountId = hasDiscount
      ? discounts?.find((discount) => discount?.active === true)?.id
      : "";
    const requestData: EmbedTokenRequest = {
      buyer,
      shipping_details: shippingDetails,
      line_item_ids: lineItemIDs,
      futurhealth_plus_id: futurhealthPlusId,
      product_id: id,
      discount_id: discountId,
    };

    const response: AxiosResponse<SecureCheckoutResponse> =
      await axiosInstance.post(
        "/v1/gr4vy/secure-fields-checkout",
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

    return { res: response.data, medicationName };
  } catch (error) {
    console.error("Error getting secure field checkout:", error);
    throw error;
  }
}

async function createTransaction(
  user: User | null,
  transaction: TransactionRequest
): Promise<TransactionResponse> {
  try {
    if (!user) throw new Error("User is not authenticated");

    const authToken = await user.getIdToken();

    const response: AxiosResponse<TransactionResponse> =
      await axiosInstance.post("/v1/gr4vy/transactions", transaction, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

    return response.data;
  } catch (error) {
    // return error message from response if available, otherwise return generic error message
    const errorResponse =
      axios.isAxiosError(error) && error.response
        ? error.response.data.error
        : "Unknown error";
    console.error("Error creating transaction:", error);
    throw errorResponse;
  }
}

/**
 * Get transactions for a user
 * @param user The current authenticated user
 * @param medicationOnly Flag to filter transactions for medication only
 * @returns List of transactions
 */
async function getTransactions(
  user: User | null,
  medicationOnly?: boolean
): Promise<TransactionData[]> {
  try {
    if (!user) throw new Error("User is not authenticated");

    const authToken = await user.getIdToken();

    // Get user data to retrieve gr4vyId
    const userData = await getUserData({ user });
    const gr4vyId = userData.data[0]?.id;

    if (!gr4vyId) throw new Error("Gr4vy ID not found for the user");

    const response: AxiosResponse<GetTransactionsResponse> =
      await axiosInstance.get(`/v1/transactions`, {
        params: {
          user_id: gr4vyId,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

    let transactions = response.data.data;

    if (medicationOnly) {
      transactions = transactions.filter(
        (transaction) => transaction.metadata.type === "medication"
      );
    }

    return transactions;
  } catch (error) {
    console.error("Error getting transactions:", error);
    throw error;
  }
}

/**
 * Fetches transactions for a specific Gr4vy user ID.
 * @param gr4vyUserId - The Gr4vy user ID.
 * @param authToken - The authentication token.
 * @returns The list of transactions for the user.
 */
const fetchTransactionsByGr4vyUserId = async (
  gr4vyUserId: string,
  authToken: string,
  signal?: AbortSignal
): Promise<GetTransactionsResponse> => {
  // Fetch transactions for a specific Gr4vy user ID using the provided auth token
  const response: AxiosResponse<GetTransactionsResponse> =
    await axiosInstance.get(`/v1/transactions`, {
      params: {
        user_id: gr4vyUserId,
      },
      headers: {
        Authorization: `Token ${authToken}`,
      },
      signal,
    });

  return response.data;
};

const getProducts = async (
  params: { [key: string]: string },
  token: string
): Promise<ProductDataResponse> => {
  const response: AxiosResponse<ProductDataResponse> = await axiosInstance.get(
    "/v1/products",
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

const initApplePaySession = async (validationURL: string, user: User) => {
  const token = await user.getIdToken();
  const response: AxiosResponse = await axiosInstance.post(
    "/v1/gr4vy/digital-wallets/apple/session",
    {
      validation_url: validationURL,
      domain_name: "plus.fh.co",
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

const initGooglePaySession = async (
  user: User
): Promise<GoogleSessionResponse> => {
  const token = await user.getIdToken();
  const response: AxiosResponse = await axiosInstance.post(
    "/v1/gr4vy/digital-wallets/google/session",
    {
      origin_domain: "plus.fh.co",
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Gets all payment methods for a user.
 * @param user - The current authenticated user.
 * @param buyerId - The ID of the Gr4vy buyer.
 * @returns The listAllPaymentMethods data response.
 */

const listAllPaymentMethods = async (
  user: User
): Promise<{ data: PaymentMethod[] }> => {
  try {
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const response: AxiosResponse<{ data: PaymentMethod[] }> =
      await axiosInstance.get("/v1/payment-methods", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

const listAllGr4vyPaymentMethods = async (
  user: User,
  buyerId: string
): Promise<ListAllGr4vyPaymentMethodsResponse> => {
  try {
    if (!user) throw new Error("User is not authenticated");
    if (!buyerId) throw new Error("BuyerID is required");

    const token = await user.getIdToken();

    const response: AxiosResponse<ListAllGr4vyPaymentMethodsResponse> =
      await axiosInstance.get(`/v1/gr4vy/payment-methods?buyerId=${buyerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

const mergePaymentMethodsData = (
  cards: { data: PaymentMethod[] },
  gr4vyCards: ListAllGr4vyPaymentMethodsResponse
) => {
  const gr4vyMap = new Map(gr4vyCards.items.map((pm) => [pm.id, pm]));

  return cards.data.map((pm) => {
    const gr4vyInfo = gr4vyMap.get(pm.external_id);

    return {
      ...pm,
      ...gr4vyInfo, // If gr4vyInfo is found, spread its properties
      external_id: pm.external_id, // Ensures external_id is taken from PaymentMethod
      // Provide default values if gr4vyInfo is missing
      additional_schemes: gr4vyInfo?.additional_schemes || [],
      approval_url: gr4vyInfo?.approval_url || null,
      cit_last_used_at: gr4vyInfo?.cit_last_used_at || "",
      cit_usage_count: gr4vyInfo?.cit_usage_count || 0,
      country: gr4vyInfo?.country || "",
      currency: gr4vyInfo?.currency || null,
      expiration_date: gr4vyInfo?.expiration_date || "",
      has_replacement: gr4vyInfo?.has_replacement || false,
      label: gr4vyInfo?.label || "",
      last_replaced_at: gr4vyInfo?.last_replaced_at || null,
      last_used_at: gr4vyInfo?.last_used_at || "",
      merchant_account_id: gr4vyInfo?.merchant_account_id || "",
      method: gr4vyInfo?.method || "",
      scheme: gr4vyInfo?.scheme || "",
      type: gr4vyInfo?.type || "",
      usage_count: gr4vyInfo?.usage_count || 0,
    };
  });
};

const createGr4vyCheckoutSession = async (
  user: User,
  buyerId: string
): Promise<CheckoutSessionResponse> => {
  try {
    if (!user) throw new Error("User is not authenticated");
    if (!buyerId) throw new Error("BuyerID is required");

    const token = await user.getIdToken();

    const requestBody = {
      // Example values, update them as needed
      expires_in: 1800, // Expiry time in seconds (30 minutes)
    };

    const response: AxiosResponse<CheckoutSessionResponse> =
      await axiosInstance.post(
        `/v1/gr4vy/checkout/sessions/proxy`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

const vaultPaymentMethod = async (
  external_id: string,
  user: User,
  gr4vy_id: string,
  primary: boolean = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  try {
    if (!external_id) throw new Error("Payment method ID is required");
    if (!gr4vy_id) throw new Error("Gr4vy ID is required");
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const requestBody = {
      external_id,
      user_id: gr4vy_id,
      primary,
    };

    const response: AxiosResponse<CheckoutSessionResponse> =
      await axiosInstance.post(`/v1/payment-methods`, requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

const paymentMethodSetPrimary = async (
  payment_method_id: string,
  user: User,
  gr4vy_id: string,
  primary: boolean = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  try {
    if (!payment_method_id) throw new Error("Payment method ID is required");
    if (!gr4vy_id) throw new Error("Gr4vy ID is required");
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const requestBody = {
      primary,
    };

    const response: AxiosResponse<CheckoutSessionResponse> =
      await axiosInstance.patch(
        `/v1/payment-methods/${payment_method_id}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

const deletePaymentMethod = async (
  payment_method_id: string,
  user: User
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  try {
    if (!payment_method_id) throw new Error("Payment method ID is required");
    if (!user) throw new Error("User is not authenticated");

    const token = await user.getIdToken();

    const response: AxiosResponse<CheckoutSessionResponse> =
      await axiosInstance.delete(`/v1/payment-methods/${payment_method_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

    return response.data;
  } catch (error) {
    console.error("Error getting user data:", error);
    throw error;
  }
};

/**
 * Gets the JWT token for checkout.
 * @param requestBody - The request body containing required parameters.
 * @returns The JWT token for checkout.
 */
const getCheckoutToken = async (
  requestBody: JwtCheckoutData,
  user: User | null
): Promise<string> => {
  if (!user?.uid)
    throw new Error("Firebase authentication is required for checkout");

  try {
    const idToken = await user.getIdToken();
    if (!idToken.trim()) throw new Error("Missing Firebase ID token");

    const response: AxiosResponse<{ token: string }> = await axiosInstance.post(
      "/v1/checkout/token",
      { ...requestBody, user_id: user.uid },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    return response.data.token;
  } catch {
    // Axios errors contain the authenticated request, including its header and
    // intake data. Callers may log the error, so expose only a fixed message.
    throw new Error("Failed to get checkout token");
  }
};

/**
 * Gets shipping details for a buyer.
 * @param token - The token to authenticate the request.
 * @returns The shipping details response.
 */
const getBuyerShippingDetails = async (
  token: string,
  signal?: AbortSignal
): Promise<ShippingDetails> => {
  try {
    if (!token) throw new Error("Token is required");

    const response: AxiosResponse<{ items: ShippingDetails[] }> =
      await axiosInstance.get("/v1/gr4vy/buyers/shipping-details", {
        headers: {
          Authorization: `Token ${token}`,
        },
        signal,
      });

    // Return the first item in the response data
    return response.data.items[0];
  } catch (error) {
    console.error("Error getting buyer shipping details:", error);
    throw error;
  }
};

/**
 * Validates a post-purchase account completion token and returns a fresh Firebase custom token.
 * Called during re-auth when auth/requires-recent-login fires on the /account page
 * for users who arrived via the post-purchase flow (firebase_token + accountCompletionToken in URL).
 */
const validatePostPurchaseAccountToken = async (
  token: string
): Promise<{ firebaseToken: string }> => {
  try {
    const response = await axiosInstance.post<{ firebaseToken: string }>(
      "/v1/checkout/validate-account-token",
      { token }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export {
  createTransaction,
  getTransactions,
  getGr4vyEmbedToken,
  getUserData,
  getProductData,
  getProductById,
  getSecureFieldCheckout,
  getProducts,
  initApplePaySession,
  initGooglePaySession,
  listAllPaymentMethods,
  listAllGr4vyPaymentMethods,
  mergePaymentMethodsData,
  createGr4vyCheckoutSession,
  vaultPaymentMethod,
  paymentMethodSetPrimary,
  deletePaymentMethod,
  getCheckoutToken,
  getBuyerShippingDetails,
  fetchTransactionsByGr4vyUserId,
  validatePostPurchaseAccountToken,
};
